'use client';

import React, { useState, useEffect } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

type ContactFields = {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
};

type Errors = Partial<Record<keyof ContactFields, string>>;

const INITIAL: ContactFields = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
};

function validateField(name: keyof ContactFields, value: string): string {
  switch (name) {
    case 'name':
      return !value.trim() ? 'Your Name is required' : '';
    case 'email':
      if (!value.trim()) return 'Your Email is required';
      if (!/\S+@\S+\.\S+/.test(value)) return 'Enter a valid email address';
      return '';
    case 'subject':
      return !value.trim() ? 'Subject is required' : '';
    case 'message':
      return !value.trim() ? 'Your Message is required' : '';
    default:
      return '';
  }
}

export default function ContactPage() {
  const [formData, setFormData] = useState(INITIAL);
  const [errors, setErrors] = useState({} as Errors);
  const [touched, setTouched] = useState({} as Partial<Record<keyof ContactFields, boolean>>);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [siteSettings, setSiteSettings] = useState<any>({});

  useEffect(() => {
    fetch('/api/settings')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setSiteSettings(data);
      })
      .catch(console.error);
  }, []);

  const handleBlur = (name: keyof ContactFields) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const err = validateField(name, formData[name]);
    setErrors((prev) => ({ ...prev, [name]: err }));
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof ContactFields;
    setFormData((prev) => ({ ...prev, [fieldName]: value }));

    if (touched[fieldName]) {
      const err = validateField(fieldName, value);
      setErrors((prev) => ({ ...prev, [fieldName]: err }));
    }
  };

  const validateAll = (): Errors => {
    const required: (keyof ContactFields)[] = ['name', 'email', 'subject', 'message'];
    const newErrors: Errors = {};
    const newTouched: Partial<Record<keyof ContactFields, boolean>> = {};
    required.forEach((field) => {
      const err = validateField(field, formData[field]);
      if (err) newErrors[field] = err;
      newTouched[field] = true;
    });
    setTouched((prev) => ({ ...prev, ...newTouched }));
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validateAll();
    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    try {
      const response = await fetch('/api/submissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          form_type: 'contact',
          name: formData.name,
          email: formData.email,
          phone: formData.phone || 'N/A',
          details: {
            subject: formData.subject,
            message: formData.message,
          },
        }),
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        console.error('Failed to submit message');
      }
    } catch (err) {
      console.error('Submission error:', err);
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (name: keyof ContactFields) =>
    `w-full px-4 py-3 text-[14px] bg-[#f7f8f9] border rounded focus:outline-none transition-colors duration-200 placeholder-gray-400 ${
      errors[name] && touched[name]
        ? 'border-red-400 bg-red-50/30 focus:border-red-500'
        : touched[name] && !errors[name] && ['name', 'email', 'subject', 'message'].includes(name)
        ? 'border-green-400 bg-green-50/20 focus:border-[#006838]'
        : 'border-gray-200 focus:border-[#006838]'
    }`;

  const renderError = (name: keyof ContactFields) => {
    if (touched[name] && errors[name]) {
      return (
        <p className="text-[11px] text-red-500 font-semibold mt-1 flex items-center gap-1 animate-in slide-in-from-top-1 duration-200">
          <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className="shrink-0 mt-px">
            <circle cx="5" cy="5" r="5" opacity="0.15" />
            <text x="5" y="7.5" textAnchor="middle" fontSize="7" fontWeight="bold" fill="currentColor">!</text>
          </svg>
          {errors[name]}
        </p>
      );
    }
    return null;
  };

  const facebookUrl = siteSettings.social_facebook || 'https://www.facebook.com/profile.php?id=61576977760490&mibextid=wwXIfr&rdid=AgGXJKSd2WrMAr7X&share_url=https%3A%2F%2Fwww.facebook.com%2Fshare%2F1F4UC9oq3g%2F%3Fmibextid%3DwwXIfr#';
  const instagramUrl = siteSettings.social_instagram || 'https://www.instagram.com/plotuae/';
  const youtubeUrl = siteSettings.social_youtube || 'https://www.youtube.com/';
  const linkedinUrl = siteSettings.social_linkedin || 'https://linkedin.com/company/107672220/';

  return (
    <div className="min-h-screen bg-[#f7f8f9]" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      <Header />

      {/* Page Header Banner */}
      <div 
        className="relative pt-[160px] pb-20 bg-cover bg-center overflow-hidden" 
        style={{ 
          backgroundImage: "url('/uploads/b5043599695e8e4103588496558a4648275b0acbdf710101a9e4a5d10e022730.jpg')",
          backgroundPosition: 'center 30%',
        }}
      >
        {/* Dark Elegant Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/45 z-10" />

        <div className="container mx-auto px-6 relative z-20" style={{ maxWidth: '1480px' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              {/* Subtle top tag */}
              <span className="text-[#82BC41] text-[10px] font-black uppercase tracking-[0.3em] block mb-2">
                Get In Touch
              </span>
              <h1 
                className="text-4xl md:text-5xl font-normal leading-tight text-white" 
                style={{ fontFamily: '"Times New Roman", serif' }}
              >
                Contact Us
              </h1>
              <div className="w-16 h-1 bg-[#82BC41] mt-3 rounded" />
            </div>

            {/* Breadcrumbs */}
            <div className="flex items-center gap-2 text-[11px] font-bold text-white/70 uppercase tracking-widest">
              <a href="/" className="hover:text-[#82BC41] transition-colors">
                Home
              </a>
              <span className="text-white/40">&gt;</span>
              <span className="text-white font-extrabold">Contact Us</span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Content */}
      <section className="py-16">
        <div className="container mx-auto px-6" style={{ maxWidth: '1480px' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left Column: Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-normal leading-snug mb-6 text-[#1a1a1a]" style={{ fontFamily: '"Times New Roman", serif' }}>
                  Reach out to us for our expert advice
                </h2>
                <p className="text-md text-[#50514f] mb-8 font-light">
                  For inquiries, feel free to contact our office locations:
                </p>

                <div className="space-y-6">
                  {/* Office 1 */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0 border border-slate-100">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#006838" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-1">Dubai Main Office</h3>
                      <p className="text-[#50514f] text-[14px]">
                        Office 125A, Al Hanaa Centre, Mankhool Road, Dubai, UAE.
                      </p>
                    </div>
                  </div>

                  {/* Office 2 */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0 border border-slate-100">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#006838" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-1">Business Bay Office</h3>
                      <p className="text-[#50514f] text-[14px]">
                        Office 764, Tamani Arts Building Business Bay, Dubai, UAE.
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0 border border-slate-100">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#006838" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-1">Phone</h3>
                      <p className="text-[#50514f] text-[14px]">+971 (04) 554 9077</p>
                      <p className="text-[#50514f] text-[14px]">+971 50 112 0273</p>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex gap-4 items-start">
                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm flex-shrink-0 border border-slate-100">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#006838" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1a1a1a] mb-1">Email</h3>
                      <p className="text-[#50514f] text-[14px]"><a href="mailto:contact@Plotae.ae" className="hover:text-[#006838] transition-colors">contact@Plotae.ae</a></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h3 className="font-semibold text-[#1a1a1a] mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href={facebookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Facebook"
                    className="w-10 h-10 bg-white border border-slate-100 rounded-full flex items-center justify-center shadow-sm text-slate-500 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-all duration-200"
                  >
                    <i className="fab fa-facebook-f text-[16px]"></i>
                  </a>
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Instagram"
                    className="w-10 h-10 bg-white border border-slate-100 rounded-full flex items-center justify-center shadow-sm text-slate-500 hover:bg-[#E4405F] hover:text-white hover:border-[#E4405F] transition-all duration-200"
                  >
                    <i className="fab fa-instagram text-[16px]"></i>
                  </a>
                  <a
                    href={youtubeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="YouTube"
                    className="w-10 h-10 bg-white border border-slate-100 rounded-full flex items-center justify-center shadow-sm text-slate-500 hover:bg-[#FF0000] hover:text-white hover:border-[#FF0000] transition-all duration-200"
                  >
                    <i className="fab fa-youtube text-[16px]"></i>
                  </a>
                  <a
                    href={linkedinUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="LinkedIn"
                    className="w-10 h-10 bg-white border border-slate-100 rounded-full flex items-center justify-center shadow-sm text-slate-500 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-all duration-200"
                  >
                    <i className="fab fa-linkedin-in text-[16px]"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-md border border-slate-100/60 max-w-xl lg:ml-auto w-full">
              <h2 className="text-2xl font-normal text-[#1a1a1a] mb-6" style={{ fontFamily: '"Times New Roman", serif' }}>
                Send us a message
              </h2>

              {submitted ? (
                <div className="py-12 text-center space-y-4 animate-in fade-in duration-300">
                  <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto text-[#006838] text-3xl border border-green-100">
                    ✓
                  </div>
                  <h3 className="text-xl font-semibold text-[#1a1a1a]" style={{ fontFamily: '"Times New Roman", serif' }}>
                    Message Sent!
                  </h3>
                  <p className="text-[13px] text-[#50514f] max-w-xs mx-auto leading-relaxed">
                    Thank you for reaching out. A plot expert from our team will get in touch with you shortly.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData(INITIAL);
                      setErrors({});
                      setTouched({});
                    }}
                    className="text-[12px] font-semibold text-[#006838] hover:underline"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                  {/* Grid Name + Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="Your Name *"
                        value={formData.name}
                        onChange={handleChange}
                        onBlur={() => handleBlur('name')}
                        className={inputClass('name')}
                      />
                      {renderError('name')}
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="Your Email *"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={() => handleBlur('email')}
                        className={inputClass('email')}
                      />
                      {renderError('email')}
                    </div>
                  </div>

                  {/* Phone + Subject */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="Phone Number (Optional)"
                        value={formData.phone}
                        onChange={handleChange}
                        className={inputClass('phone')}
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="subject"
                        placeholder="Subject *"
                        value={formData.subject}
                        onChange={handleChange}
                        onBlur={() => handleBlur('subject')}
                        className={inputClass('subject')}
                      />
                      {renderError('subject')}
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <textarea
                      name="message"
                      placeholder="Your Message *"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      onBlur={() => handleBlur('message')}
                      className={`${inputClass('message')} resize-none`}
                    ></textarea>
                    {renderError('message')}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#006838] text-white py-3.5 rounded font-bold hover:bg-[#00522c] transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Message'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Google Map Section */}
      <section className="h-[450px] bg-slate-100 relative w-full border-t border-slate-200">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3608.482!2d55.2966!3d25.2048!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjXCsDEyJzE3LjMiTiA1NcKwMTcnNDcuOCJF!5e0!3m2!1sen!2sae!4v1620000000000!5m2!1sen!2sae"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Plotae Dubai Location Map"
        />
      </section>

      <Footer />
    </div>
  );
}
